import {union} from 'lodash-es';

/** @module utils/determineProductOfferings */

/**
 * Parameters for determining available product offerings.
 */
interface DetermineProductOfferingsParams {
    filteredFacilities: Array<{
        productOfferings: string[];
        compPassesEnabled?: boolean;
        employeeParkingEnabled?: boolean;
    }>;
}

/**
 * Result indicating whether specific product offerings are enabled.
 */
interface ProductOfferings {
    transientEnabled: boolean;
    monthlyEnabled: boolean;
    airportEnabled: boolean;
    airportFlexEnabled: boolean;
    eventEnabled: boolean;
    compPassesEnabled: boolean;
    employeeParkingEnabled: boolean;
}

/**
 * Determines the available product offerings across a set of facilities.
 *
 * @param {DetermineProductOfferingsParams} params - The input parameters.
 * @returns {ProductOfferings} An object indicating whether specific product offerings are enabled.
 */
const determineProductOfferings = ({
    filteredFacilities,
}: DetermineProductOfferingsParams): ProductOfferings => {
    const productOfferingsArray = filteredFacilities.reduce<string[]>(
        (offerings, facility) => {
            const {
                productOfferings,
                compPassesEnabled,
                employeeParkingEnabled,
            } = facility;

            const updatedOfferings =
                employeeParkingEnabled || compPassesEnabled
                    ? [
                          ...productOfferings,
                          ...(employeeParkingEnabled
                              ? ['employee_parking']
                              : []),
                          ...(compPassesEnabled ? ['comp_pass'] : []),
                      ]
                    : productOfferings;

            return union(updatedOfferings, offerings);
        },
        []
    );

    return {
        transientEnabled: productOfferingsArray.includes('transient'),
        monthlyEnabled: productOfferingsArray.includes('monthly'),
        airportEnabled: productOfferingsArray.includes('airport'),
        airportFlexEnabled: productOfferingsArray.includes('airport_flex'),
        eventEnabled: productOfferingsArray.includes('events'),
        compPassesEnabled: productOfferingsArray.includes('comp_pass'),
        employeeParkingEnabled:
            productOfferingsArray.includes('employee_parking'),
    };
};

export default determineProductOfferings;
